<template>
  <div class="flex flex-col w-full h-full gap-2 overflow-y-auto">
    <span class="mb-1 font-medium">לקוח</span>
    <SelectCustomer @customer-selected="onCustomerSelected" />

    <div class="flex justify-between items-center mt-2 mb-1">
      <span class="font-medium">מוצרים</span>
      <Button
        v-if="canScanBarcode"
        icon="pi pi-barcode"
        label="סריקת מוצר"
        size="small"
        outlined
        @click="scanProduct" />
    </div>

    <div class="w-full">
      <IconField>
        <InputIcon class="pi pi-search" />
        <InputText size="small" v-model="searchValue" placeholder="חיפוש מוצר" class="w-full" />
      </IconField>
    </div>

    <!-- Selected items -->
    <div v-if="cart.length" class="flex flex-col gap-2 pt-2">
      <div v-for="entry of cart" :key="entry.item.id"
           class="flex justify-between items-center border border-gray-200 rounded-xl p-3">
        <div class="flex flex-col">
          <span class="font-medium text-sm">{{ entry.item.name }}</span>
          <span class="text-xs text-gray-500">₪{{ entry.item.discountedPrice ?? entry.item.price }} ליחידה</span>
        </div>
        <div class="flex items-center gap-2">
          <Button icon="pi pi-minus" size="small" text rounded @click="changeAmount(entry.item.id, -1)" />
          <span class="w-6 text-center font-medium">{{ entry.amount }}</span>
          <Button icon="pi pi-plus" size="small" text rounded @click="changeAmount(entry.item.id, 1)" />
        </div>
      </div>
    </div>

    <!-- Available products -->
    <div class="flex flex-col gap-2 pt-2">
      <div v-for="product of filteredAvailableProducts" :key="product.id"
           class="flex justify-between items-center border border-gray-100 rounded-xl p-3 cursor-pointer hover:bg-gray-50"
           @click="changeAmount(product.id, 1)">
        <div class="flex flex-col">
          <span class="text-sm">{{ product.name }}</span>
          <span class="text-xs text-gray-500">
            ₪{{ product.discountedPrice ?? product.price }}
            <template v-if="product.availableStock !== undefined && product.availableStock !== null"> · {{ product.availableStock }} במלאי</template>
          </span>
        </div>
        <i class="pi pi-plus text-gray-400" />
      </div>
    </div>

    <div class="flex justify-between items-center border-t pt-3 mt-2">
      <span>סה״כ לתשלום</span>
      <span class="font-medium">₪{{ cartSum }}</span>
    </div>

    <drawer-footer
      class="mt-auto"
      :approveDisable="!canSubmit || creating"
      :approveLoading="creating"
      approveLabel="המשך לתשלום"
      @approve-click="createSale"
      @cancel-click="$emit('close')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { ICustomer, IShopItem, TCartItem } from '@torim-app/types';
import { useToast } from 'vue-toastification';
import { useDrawer } from '@torim-app/shared-components';
import { Capacitor } from '@capacitor/core';
import InputIcon from 'primevue/inputicon';
import IconField from 'primevue/iconfield';

import { useAdminStore } from '@app/stores';
import { ProductOrdersApi } from '@app/api/admin/product-orders-api';
import SelectCustomer from '@app/components/admin/components/SelectCustomer.vue';
import PaymentOptions from '@app/components/admin/modals/PaymentOptions.vue';
import { PRODUCT_BARCODE_FORMATS, useQRScanner } from '@app/composables/qr-reader';
import { trackEvent } from '@app/composables/posthog';

const emit = defineEmits(['created', 'close']);

const toast = useToast();
const adminStore = useAdminStore();
const { openDrawer, closeDrawer } = useDrawer();
const { scanSingleBarcode } = useQRScanner();

const canScanBarcode = Capacitor.isNativePlatform();

const selectedCustomer = ref<Pick<ICustomer, 'id' | 'fullName' | 'phoneNumber'> | undefined>();
const cart = ref<Array<{ item: IShopItem; amount: number }>>([]);
const searchValue = ref('');
const creating = ref(false);

const allProducts = computed<IShopItem[]>(() =>
  (adminStore.businessSiteData?.shop?.items ?? []).filter(x => x.active));

const filteredAvailableProducts = computed(() => {
  let products = allProducts.value.filter(p => !cart.value.find(c => c.item.id === p.id));
  if (searchValue.value)
    products = products.filter(p => p.name.toLowerCase().includes(searchValue.value.toLowerCase()));
  return products;
});

const cartSum = computed(() => cart.value.reduce(
  (sum, entry) => sum + (entry.item.discountedPrice ?? entry.item.price) * entry.amount, 0));

const canSubmit = computed(() => !!selectedCustomer.value && cart.value.length > 0);

const onCustomerSelected = (customer: Pick<ICustomer, 'id' | 'fullName' | 'phoneNumber'>) => {
  selectedCustomer.value = customer;
};

const changeAmount = (productId: string, delta: 1 | -1) => {
  const entry = cart.value.find(c => c.item.id === productId);
  if (!entry) {
    const product = allProducts.value.find(p => p.id === productId);
    if (!product) return;
    if (delta === 1) cart.value.push({ item: product, amount: 1 });
    return;
  }
  entry.amount += delta;
  if (entry.amount <= 0)
    cart.value = cart.value.filter(c => c.item.id !== productId);
};

// Supermarket-style: each scan adds one unit of the matching product.
const scanProduct = async () => {
  trackEvent('manual_sale_barcode_scan_clicked');
  try {
    const barcode = await scanSingleBarcode(PRODUCT_BARCODE_FORMATS);
    const product = allProducts.value.find(p => p.barcode && p.barcode === barcode);
    if (!product) {
      toast.warning('לא נמצא מוצר עם הברקוד הזה');
      return;
    }
    if (product.availableStock !== undefined && product.availableStock !== null && product.availableStock <= 0) {
      toast.warning(`${product.name} אזל מהמלאי`);
      return;
    }
    changeAmount(product.id, 1);
    toast.success(`${product.name} נוסף למכירה`);
  } catch (e) {
    console.error(e);
    toast.error('הסריקה נכשלה, אפשר לחפש את המוצר ידנית');
  }
};

const createSale = async () => {
  if (!canSubmit.value) return;
  creating.value = true;
  try {
    trackEvent('manual_sale_created', { products_count: cart.value.length, total_amount: cartSum.value });
    const items = cart.value.map<TCartItem>(entry => ({
      id: entry.item.id,
      amount: entry.amount,
      type: 'product'
    }));
    const created = await ProductOrdersApi.createManualSale({
      customerId: selectedCustomer.value!.id,
      items
    });

    emit('created', created);
    openDrawer(
      PaymentOptions,
      {
        uuid: created.orderId,
        payFor: 'product-order',
        onClose: (shouldLoad: boolean) => {
          closeDrawer();
          if (shouldLoad) emit('created', created);
          emit('close');
        }
      },
      { title: 'אפשרויות תשלום' }
    );
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו ליצור את המכירה, אנא נסו שוב');
  } finally {
    creating.value = false;
  }
};
</script>

<style scoped lang="scss">
</style>
